import { AfterViewInit, Component, ElementRef, HostListener, NgZone, OnDestroy, ViewChild } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { environment } from 'src/environments/environment';
import { Casa, PuntoCasa, Visor360 } from '../home-piloto/visor-360';

/*
 * Sección del home: calle de noche con los inmuebles del sorteo dibujados por piezas (SVG).
 * Al pasar el cursor se encienden; al hacer clic la escena hace zoom al inmueble y se abre su
 * recorrido a pantalla completa AHÍ MISMO (sin cambiar de página). `recorrido`:
 *   fotos      -> fotos 360 de assets/recorrido/casa-ejemplo (mismo visor que /home-piloto)
 *   matterport -> la casa escaneada (mismo modelo que /home-piloto-2)
 * Los que aún no tienen recorrido se encienden pero no entran.
 * Rescatada del prototipo original de /home-piloto (commit 92ac065), sin el recorrido Pannellum.
 * Cuando diseño entregue el recorte PNG de un inmueble, se pone en `imagen` y reemplaza al SVG.
 */

const CARPETA_CASA = 'assets/recorrido/casa-ejemplo/';
const MODELO_MATTERPORT = 'daTHxukBe25';

interface Ventana { x: number; y: number; w: number; h: number; d: number; }

interface Premio {
	id: string;
	lugar: string;
	ciudad: string;
	desarrollo: string;
	tipo: string;
	valor: string;
	left: number;
	bottom: number;
	width: number;
	vb: number[];
	lado: 'izq' | 'der' | 'arriba';
	imagen?: string;
	/** tipo de recorrido; sin él el inmueble queda como "próximamente" */
	recorrido?: 'fotos' | 'matterport';
}

@Component({
	selector: 'app-premios-calle',
	templateUrl: './premios-calle.component.html',
	styleUrls: ['./premios-calle.component.scss']
})
export class PremiosCalleComponent implements AfterViewInit, OnDestroy {

	/* ventanas de las torres: se calculan una sola vez */
	ventanasEdomex = this.rejilla(164, 92, 6, 27, 22, 11, 6, 5);
	ventanasCancun = this.rejilla(108, 74, 5, 17, 24, 15, 9, 11);
	ventanasCancun2 = this.rejilla(284, 152, 3, 14, 18, 15, 8, 11);
	losasCancun = Array.from({ length: 18 }, (_, i) => 66 + i * 26);
	estrellas = Array.from({ length: 60 }, (_, i) => ({
		x: (i * 37) % 100,
		y: (i * 53) % 45,
		t: 1 + (i % 3),
		d: (i * 0.37) % 4
	}));
	faroles = [8, 22, 36, 50, 64, 78, 92];

	premios: Premio[] = [
		{
			id: 'edomex', lugar: '1°', ciudad: 'Edo. México', desarrollo: 'Bosque Real Five', tipo: 'Departamento amueblado',
			valor: '13.5', left: 1, bottom: 7, width: 26, vb: [500, 620], lado: 'der', recorrido: 'fotos'
		},
		{
			id: 'merida', lugar: '3°', ciudad: 'Mérida', desarrollo: 'El Origen', tipo: 'Casa amueblada',
			valor: '6.6', left: 28.5, bottom: 9, width: 13.5, vb: [300, 260], lado: 'arriba'
		},
		{
			id: 'puebla', lugar: '4°', ciudad: 'Puebla', desarrollo: 'El Capricho', tipo: 'Casa amueblada',
			valor: '6.2', left: 45, bottom: 9, width: 11, vb: [260, 300], lado: 'arriba'
		},
		{
			id: 'queretaro', lugar: '5°', ciudad: 'Querétaro', desarrollo: 'Zibatá', tipo: 'Casa amueblada',
			valor: '5.4', left: 60.5, bottom: 9, width: 9.5, vb: [220, 200], lado: 'arriba'
		},
		{
			id: 'cancun', lugar: '2°', ciudad: 'Cancún', desarrollo: 'Brezza Towers Mar', tipo: 'Departamento amueblado',
			valor: '13.0', left: 75, bottom: 7, width: 23, vb: [420, 560], lado: 'izq', recorrido: 'matterport'
		}
	];

	hover: Premio | null = null;
	seleccionado: Premio | null = null;
	entrando: Premio | null = null;
	saliendo = false;
	origen = '50% 50%';

	@ViewChild('scroll') scrollRef: ElementRef<HTMLElement>;
	@ViewChild('lienzoTour') lienzoTour?: ElementRef<HTMLDivElement>;

	/* recorrido abierto */
	urlCompra = environment.URL_MICROSITIO;
	urlMatterport: SafeResourceUrl;
	carpeta = CARPETA_CASA;
	abierto: Premio | null = null;
	casa?: Casa;
	punto?: PuntoCasa;
	tourListo = false;
	pista = true;

	private tour?: Visor360;
	private timers: any[] = [];

	constructor(private zone: NgZone, sanitizer: DomSanitizer) {
		this.urlMatterport = sanitizer.bypassSecurityTrustResourceUrl(
			`https://my.matterport.com/show?m=${MODELO_MATTERPORT}&play=1&lang=es&brand=0&title=0&help=0&mls=0&ts=3&lp=1`);
	}

	ngAfterViewInit(): void {
		// en pantallas angostas la escena se desliza: arranca centrada en las casas
		const scroll = this.scrollRef.nativeElement;
		scroll.scrollLeft = (scroll.scrollWidth - scroll.clientWidth) / 2;
	}

	ngOnDestroy(): void {
		this.timers.forEach(clearTimeout);
		if (this.tour) this.tour.destruir();
		document.body.style.overflow = '';
	}

	get esTactil(): boolean {
		return window.matchMedia && window.matchMedia('(hover: none)').matches;
	}

	altoPct(p: Premio): number {
		return p.width * (p.vb[1] / p.vb[0]) * (1900 / 700);
	}

	tocar(p: Premio): void {
		if (this.entrando) return;
		// en touch: primer toque "enciende", el segundo entra
		if (this.esTactil && this.seleccionado !== p) {
			this.seleccionado = p;
			return;
		}
		if (p.recorrido) this.entrar(p);
	}

	/** zoom hacia el inmueble y, al terminar, se abre su recorrido encima del home */
	private entrar(p: Premio): void {
		const cx = p.left + p.width / 2;
		const cy = 100 - p.bottom - this.altoPct(p) * 0.22;
		this.origen = `${cx}% ${cy}%`;
		this.entrando = p;
		this.hover = null;
		if (p.recorrido === 'fotos' && !this.casa) {
			// se pide mientras corre el zoom para que no haya espera al entrar
			fetch(CARPETA_CASA + 'casa.json').then((r) => r.json()).then((c) => (this.casa = c)).catch(() => { });
		}
		this.timers.push(setTimeout(() => this.abrir(p), 950));
	}

	private async abrir(p: Premio): Promise<void> {
		this.abierto = p;
		this.entrando = null;
		this.seleccionado = null;
		this.tourListo = false;
		this.pista = true;
		this.punto = undefined;
		document.body.style.overflow = 'hidden';
		if (p.recorrido !== 'fotos') return;
		if (!this.casa) {
			try {
				this.casa = await (await fetch(CARPETA_CASA + 'casa.json')).json();
			} catch {
				this.cerrar();
				return;
			}
		}
		// esperar a que el *ngIf pinte el lienzo
		setTimeout(() => {
			if (!this.lienzoTour || this.abierto !== p) return;
			this.zone.runOutsideAngular(() => {
				this.tour = new Visor360(this.lienzoTour!.nativeElement, {
					base: CARPETA_CASA, casa: this.casa!,
					alCargar: () => this.zone.run(() => (this.tourListo = true)),
					alCambiar: (pt) => this.zone.run(() => (this.punto = pt)),
				});
				this.tour.iniciar(this.casa!.puntos[0].id);
			});
			this.timers.push(setTimeout(() => (this.pista = false), 6000));
		});
	}

	cerrar(): void {
		if (this.tour) this.tour.destruir();
		this.tour = undefined;
		this.abierto = null;
		document.body.style.overflow = '';
		// la calle regresa con el zoom inverso
		this.saliendo = true;
		this.timers.push(setTimeout(() => (this.saliendo = false), 1000));
	}

	ir(p: PuntoCasa): void {
		this.pista = false;
		if (this.tour) this.tour.irA(p.id);
	}

	@HostListener('document:keydown.escape')
	escape(): void {
		if (this.abierto) this.cerrar();
	}

	get puntosPlano(): PuntoCasa[] {
		return this.casa ? this.casa.puntos.filter((p) => p.plano) : [];
	}

	/** líneas del plano entre puntos conectados (calculadas una vez) */
	private _lineas?: number[][];
	get lineas(): number[][] {
		if (!this._lineas && this.casa) {
			const pos = new Map(this.puntosPlano.map((p) => [p.id, p.plano!] as [string, number[]]));
			const vistas = new Set<string>();
			this._lineas = [];
			for (const p of this.puntosPlano) for (const c of p.conexiones) {
				const k = [p.id, c.a].sort().join('|');
				const b = pos.get(c.a);
				if (b && !vistas.has(k)) { vistas.add(k); this._lineas.push([p.plano![0], p.plano![1], b[0], b[1]]); }
			}
		}
		return this._lineas || [];
	}

	private rejilla(x0: number, y0: number, cols: number, filas: number, w: number, h: number, gx: number, gy: number): Ventana[] {
		const v: Ventana[] = [];
		for (let f = 0; f < filas; f++) {
			for (let c = 0; c < cols; c++) {
				const i = f * cols + c;
				v.push({ x: x0 + c * (w + gx), y: y0 + f * (h + gy), w, h, d: (i * 137) % 700 });
			}
		}
		return v;
	}
}
